// ** React Imports
import { useState, useEffect } from "react";

// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  Tooltip,
  FormLabel,
  FormControlLabel,
  Checkbox,
  FormHelperText,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// ** Icon Imports
import Icon from "components/icon";

// ** Custom Component Imports
import CustomTextField from "components/mui/text-field";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// ** Toast Imports
import 'react-toastify/dist/ReactToastify.css';

// ** Logic Imports
import { useAddAssetTypeLogic } from "../logic/AddAssetTypeLogic";
import { useInventoryPermission } from "../permissions/InventoryPermissions";

const AddAssetType = ({ open, handleClose, setLoading, selectedAssetType, onItemCreated }) => {
  //#region Permissions
  const { InventoryPermissionProps } = useInventoryPermission();
  const {
    hasTypeCatalogSavePermission,
  } = InventoryPermissionProps;
  //#endregion Permissions

  const theme = useTheme();

  const {
    assetTypeForm,
    errors,
    handleInputChange,
    handleSaveAssetType,
    resetForm,
  } = useAddAssetTypeLogic({
    handleClose,
    setLoading,
    open,
    selectedAssetType,
  });

  return (
    <Dialog
      fullWidth={true}
      maxWidth="sm"
      open={open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #D9D9D9",
          paddingBottom: "20px !important",
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {selectedAssetType ? "Edit Asset Type" : "Create New Asset Type"}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
      <Grid container spacing={5}>
        <Grid item xs={12} md={7}>
          <CustomTextField
            fullWidth
            label={
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  "& .MuiFormLabel-asterisk": { display: "none" },
                }}
              >
                <Box
                  component="span"
                  sx={{ display: "inline-flex", alignItems: "center" }}
                >
                  Asset Type
                  <Box component="span" sx={{ color: "error.main", ml: 0.2 }}>
                    *
                  </Box>
                </Box>
              </Box>
            }
            placeholder="Enter the asset type name"
            name="typeName"
            value={assetTypeForm.typeName}
            onChange={handleInputChange}
            error={Boolean(errors.typeName)}
            helperText={errors.typeName}
          />
        </Grid>
              <Grid item xs={12} md={8}>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="success"
                        name="isVehicle"
                        checked={assetTypeForm.isVehicle}
                        onChange={handleInputChange}
                      />
                    }
                    label="Vehicle"
                  />
                  <FormHelperText sx={{ ml: 6, mt: -1 }}>
                    Check this box to add  License Plate field to assets of this type
                  </FormHelperText>
                </Box>
              </Grid>
                <Grid item xs={12} md={7}>
                  <CustomTextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Description"
                    placeholder="Enter a brief description"
                    name="description"
                    value={assetTypeForm.description}
                    onChange={handleInputChange}
                  />
                </Grid>
              </Grid>

            </DialogContent>
            <DialogActions
              sx={{
                display: "flex",
                justifyContent: "space-between",
                padding: "20px 24px !important",
                boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
                zIndex: 1,
              }}
              className="dialog-actions-dense"
            >
              <Box>
                <Button variant="contained" color="info" onClick={handleClose}>
                  Cancel
                </Button>
              </Box>
              <Box>
                <Button
                  variant="contained"
                  color="main"
                  disabled={!hasTypeCatalogSavePermission}
                  {...(hasTypeCatalogSavePermission && {
                    onClick: async () => {
                      const result = await handleSaveAssetType();
                      // Call optional callback if provided and save was successful
                      if (result && result.success && onItemCreated && result.data) {
                        onItemCreated(result.data);
                      }
                    }
                  })}
                >
                  {selectedAssetType ? "Save Changes" : "Save Asset Type"}
                </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AddAssetType;